<template>
  <div
    v-if="pass"
    class="v3kiosk-active-pass"
    data-testid="kiosk-ended-pass-status"
    :data-variant="variant"
  >
    <CRow class="justify-content-center">
      <hr class="kiosk-horizontal-line mb-2" />
      <div
        class="v3kiosk-active-pass-title mb-2"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <div>{{ pageTitle }}</div>
        <div class="sr-only">{{ statusTitle }}</div>
      </div>

      <StatusBox
        class="d-flex justify-content-center mb-2"
        :title="statusTitle"
        title-color="#ffffff"
        background-color="#008566"
        icon="ri-checkbox-circle-line"
        icon-color="#ffffff"
      />

      <CCol class="v3kiosk-active-pass-table mb-2">
        <CRow class="v3kiosk-active-pass-table-header mb-1">
          <CCol class="v3kiosk-active-pass-table-header-info text-start ms-1">
            <div class="created-at">
              {{
                $helpers.transformDate(pass.created_at, "dddd, DD, MMMM, YYYY")
              }}
            </div>
            <div class="time">
              {{ $t("kiosk.time") }}
              {{ $helpers.transformDate(pass.created_at, "h:mm a") }}
            </div>
          </CCol>
        </CRow>
        <CRow class="v3kiosk-active-pass-table-content">
          <CCol class="v3kiosk-active-pass-table-content-text">
            {{ locationName(pass.from) }}
          </CCol>
          <CCol class="v3kiosk-active-pass-table-content-icon">
            <i class="ri-arrow-go-back-line" aria-hidden="true" />
          </CCol>
          <CCol class="v3kiosk-active-pass-table-content-text">
            {{ locationName(pass.to) }}
          </CCol>
        </CRow>
        <CRow class="v3kiosk-active-pass-table-bottom mt-1">
          <CCol
            class="v3kiosk-active-pass-table-bottom-type d-flex flex-column justify-content-center align-content-center ms-1"
          >
            <div class="v3kiosk-active-pass-table-bottom-type-box">
              <div class="icon align-self-center">
                <i :class="passTypeIcon" aria-hidden="true" />
              </div>
              <div class="text">{{ passTypeLabel }}</div>
            </div>
          </CCol>
          <CCol
            v-if="tardyBadgeLabel"
            class="v3kiosk-active-pass-table-bottom-type d-flex flex-column justify-content-center align-content-center ms-1"
          >
            <div class="v3kiosk-active-pass-table-bottom-type-box">
              <div class="icon align-self-center">
                <i :class="tardyBadgeIcon" aria-hidden="true" />
              </div>
              <div class="text">{{ tardyBadgeLabel }}</div>
            </div>
          </CCol>
        </CRow>
      </CCol>

      <div class="d-flex justify-content-center mt-2">
        <BaseButton
          class="w-50 d-flex align-items-center justify-content-center"
          rounded
          data-testid="kiosk-ended-pass-close"
          :aria-label="closeButtonLabel"
          @click="close()"
        >
          {{ closeButtonText }}
        </BaseButton>
      </div>

      <div class="d-flex align-items-center justify-content-center mt-5">
        <a
          class="kiosk-terms-and-privacy-text"
          target="_blank"
          href="https://www.securly.com/terms-and-conditions"
          >{{ $t("kiosk.terms_of_use") }}</a
        >
        <span class="mx-2">|</span>
        <a
          class="kiosk-terms-and-privacy-text"
          target="_blank"
          href="https://www.securly.com/privacy"
          >{{ $t("kiosk.privacy_policy") }}</a
        >
      </div>
    </CRow>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, reactive } from "vue"
import { useI18n } from "vue-i18n"
import moment from "moment"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import StatusBox from "@/v3components/shared/Alerts/StatusBox.vue"

export default {
  name: "KioskEndedPassStatus",

  components: { BaseButton, StatusBox },

  props: {
    pass: {
      type: Object,
      default: null
    },
    variant: {
      type: String,
      default: "ended",
      validator: (value) => ["ended", "slp"].includes(value)
    }
  },

  emits: ["close"],

  setup(props, { emit }) {
    const { t } = useI18n()

    const state = reactive({
      timeout: 120,
      interval: null
    })

    const isSlpVariant = computed(() => props.variant === "slp")

    const pageTitle = computed(() => {
      return isSlpVariant.value
        ? t("kiosk.student_late_pass")
        : t("kiosk.end_pass_title")
    })

    const statusTitle = computed(() => {
      return t("kiosk.pass_ended")
    })

    const passTypeIcon = computed(() => {
      return isSlpVariant.value ? "ri-hourglass-line" : "ri-device-line"
    })

    const passTypeLabel = computed(() => {
      if (isSlpVariant.value) {
        return props.pass?.type || "SLP"
      }
      return t("kiosk.ksk")
    })

    const tardyBadgeLabel = computed(() => {
      if (!isSlpVariant.value) return ""
      if (props.pass?.tardy_badge === "LTS") {
        return t("common.late_to_school")
      }
      if (props.pass?.tardy_badge === "LTC") {
        return t("common.late_to_class")
      }
      return ""
    })

    const tardyBadgeIcon = computed(() => {
      if (props.pass?.tardy_badge === "LTS") return "ri-rest-time-line"
      if (props.pass?.tardy_badge === "LTC") return "ri-alarm-line"
      return ""
    })

    const timeoutCounter = computed(() => {
      return moment.utc(state.timeout * 1000).format("mm:ss")
    })

    const closeButtonText = computed(() => {
      return `${t("kiosk.close")}(${timeoutCounter.value})`
    })

    const closeButtonLabel = computed(() => closeButtonText.value)

    const locationName = (location) => {
      if (!location) return ""
      if (location.name) return location.name
      return `${location.first_name || ""} ${location.last_name || ""}`.trim()
    }

    const close = () => {
      if (state.interval) {
        clearInterval(state.interval)
        state.interval = null
      }
      emit("close")
    }

    const initTimeout = () => {
      if (state.interval) return

      state.interval = setInterval(() => {
        if (state.timeout > 0) {
          state.timeout--
        }
        if (state.timeout <= 0) {
          clearInterval(state.interval)
          state.interval = null
          close()
        }
      }, 1000)
    }

    onMounted(() => {
      initTimeout()
    })

    onUnmounted(() => {
      if (state.interval) {
        clearInterval(state.interval)
        state.interval = null
      }
    })

    return {
      pageTitle,
      statusTitle,
      passTypeIcon,
      passTypeLabel,
      tardyBadgeLabel,
      tardyBadgeIcon,
      timeoutCounter,
      closeButtonText,
      closeButtonLabel,
      locationName,
      close
    }
  }
}
</script>
